import { Link } from "react-router-dom";
import LegalLayout from "./LegalLayout";

export default function Terms() {
  return (
    <LegalLayout title="Terms of Service" updated="February 20, 2026" testid="terms-page">
      <p>
        These Terms of Service (&quot;Terms&quot;) govern your access to and use of Clinico
        Translate (&quot;Service&quot;), a product of Wellness and Lifestyle Strategies LLC
        (&quot;we&quot;, &quot;us&quot;), operated at clinicotranslate.com. By creating an account
        or using the Service, you agree to these Terms.
      </p>

      <h2>1. Who can use the Service</h2>
      <p>
        The Service is intended for licensed occupational, speech, and physical therapists, their
        assistants, and the clinics that employ them. You must be at least 18 years old and legally
        able to enter into this agreement. If you use the Service on behalf of a clinic or employer,
        you represent that you have authority to bind that entity to these Terms.
      </p>

      <h2>2. Your account</h2>
      <ul>
        <li>You are responsible for the security of your login credentials.</li>
        <li>Do not share your account with other clinicians — each licensed user needs their own seat.</li>
        <li>You must promptly notify us of any unauthorized use of your account.</li>
      </ul>

      <h2>3. Subscription plans &amp; billing</h2>
      <ul>
        <li>Paid plans are billed monthly or annually through Stripe.</li>
        <li>Subscriptions renew automatically until canceled. You can cancel any time from the Billing page; cancellation stops future charges and access continues through the end of the paid period.</li>
        <li>All fees are non-refundable except where required by law.</li>
        <li>We may adjust pricing with at least 30 days notice for the next billing cycle.</li>
      </ul>

      <h2>4. Clinical &amp; safety disclaimer</h2>
      <p>
        Clinico Translate is a communication aid — <strong>not</strong> a medical device, diagnostic
        tool, or substitute for a certified medical interpreter. AI translations may contain errors
        or omissions. You, the licensed clinician, remain responsible for clinical judgment,
        documentation, informed consent, and for using a qualified human interpreter whenever the
        clinical or legal situation requires it.
      </p>

      <h2>5. Patient data &amp; HIPAA</h2>
      <p>
        You represent that you have all necessary authorizations before entering, recording, or
        translating any patient information via the Service. See our{" "}
        <Link to="/privacy">Privacy Policy</Link> for details on how patient data is handled. We
        operate the Service in a HIPAA-ready manner, but until Business Associate Agreements are in
        place with every downstream sub-processor you should minimize the PHI you enter into the
        Service and follow your employer&#39;s policies.
      </p>

      <h2>6. Acceptable use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Use the Service to violate any law or regulation, including HIPAA, HITECH, state privacy laws, or export controls.</li>
        <li>Attempt to reverse-engineer, scrape, or resell the Service.</li>
        <li>Upload malicious code or attempt to disrupt other users&#39; access.</li>
        <li>Use the Service in any life-critical or emergency-care setting where translation errors could cause immediate harm without a human interpreter present.</li>
        <li>Impersonate another clinician or misrepresent your credentials.</li>
      </ul>

      <h2>7. Intellectual property</h2>
      <p>
        The Service, including its software, branding, marketing content, and the name &quot;Clinico
        Translate&quot;<sup>™</sup>, is owned by Wellness and Lifestyle Strategies LLC and
        protected by copyright and
        trademark law. You receive a limited, non-exclusive, non-transferable, revocable license to
        use the Service for your professional practice while your subscription is active. Your
        patient records and notes remain yours.
      </p>

      <h2>8. Feedback</h2>
      <p>If you send us suggestions, we may use them without obligation or compensation.</p>

      <h2>9. Termination</h2>
      <p>
        You may close your account at any time. We may suspend or terminate access if you violate
        these Terms, fail to pay, or if we&#39;re required to by law. On termination, your account
        data will be deleted per our <Link to="/privacy">Privacy Policy</Link>.
      </p>

      <h2>10. Disclaimers</h2>
      <p>
        THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; WITHOUT WARRANTIES OF
        ANY KIND, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
        PARTICULAR PURPOSE, ACCURACY OF TRANSLATIONS, OR NON-INFRINGEMENT. WE DO NOT WARRANT THAT
        THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR CLINICALLY ACCURATE IN EVERY LANGUAGE PAIR
        OR DIALECT.
      </p>

      <h2>11. Limitation of liability</h2>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE SERVICE OPERATOR&#39;S TOTAL LIABILITY ARISING
        OUT OF OR RELATED TO THESE TERMS OR THE SERVICE WILL NOT EXCEED THE AMOUNT YOU PAID US IN
        THE 12 MONTHS PRECEDING THE CLAIM. WE ARE NOT LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL,
        CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOST PROFITS OR CLINICAL OUTCOMES.
      </p>

      <h2>12. Indemnification</h2>
      <p>
        You agree to indemnify and hold harmless Wellness and Lifestyle Strategies LLC from any claims arising from
        your use of the Service, your patient interactions, your violation of these Terms, or your
        violation of any law or third-party right.
      </p>

      <h2>13. Governing law &amp; disputes</h2>
      <p>
        These Terms are governed by the laws of the Commonwealth of Pennsylvania, without
        regard to conflict of laws principles. Any dispute will be resolved in the state or
        federal courts located in Pennsylvania, and you consent to their exclusive jurisdiction.
      </p>

      <h2>14. Changes to these Terms</h2>
      <p>
        We may update these Terms from time to time. Material changes will be announced by email or
        in-app notice at least 14 days before taking effect. Continued use of the Service after the
        effective date constitutes acceptance.
      </p>

      <h2>15. Contact</h2>
      <p>Questions about these Terms? Email <a href="mailto:BrittneyM@clinicotranslate.com">BrittneyM@clinicotranslate.com</a>.</p>
    </LegalLayout>
  );
}
